/* eslint-disable @typescript-eslint/no-explicit-any */
import { type AnalyticsServiceInterface, type AnalyticsData, Param, Event } from "./interfaces/analytics.service.interface";
import type { Domains, Firebase } from "@ca.vmedia.tv/domains";
import { useUserInfoStore } from "@/stores/UserInfo";
import type { ScreenName } from "@/common/types/screen-names";
import type { PlatformInterface } from "../platform/platform-interface";

const LOGIN_AS_GUEST = "Guest";

export class AnalyticsService implements AnalyticsServiceInterface {

    private [Param.FA_APP_NAME]: string = "";
    private [Param.DEVICE_ID]: string | null = null;

    constructor(
        private readonly firebase: Firebase,
        private readonly domains: Domains,
        private readonly platform: PlatformInterface,
    ) {
        this.firebase = firebase;
        this.domains = domains;
        this.platform = platform;
        this.init();
    }
    private init(): void {
        const { applicationName } = useAppConfig();
        this[Param.FA_APP_NAME] = applicationName;
        this.platform.getDeviceInfo().then((info) => {
            this[Param.DEVICE_ID] = info.macAddress;
        }).catch((error) => {
            console.error("Error getting platform info:", error);
        });
    }

    private async getMainParams(): Promise<Record<string, any>> {
        const { username, sessionId } = await this.domains.loadUserInfo();

        return {
            [Param.USER_NAME]: username || LOGIN_AS_GUEST,
            [Param.SESSION_ID]: sessionId || "",
            [Param.FA_APP_NAME]: this[Param.FA_APP_NAME],
            [Param.DEVICE_ID]: this[Param.DEVICE_ID],
        };
    }

    private sendEvent(eventType: Event, payload: Record<string, any>) {
        this.getMainParams().then((params) => {
            this.firebase.logEvent(this.firebase.analytics, eventType, {
                ...params,
                ...payload,
            });
        }).catch((error) => {
            console.error("Error getting main params:", error);
        });
    }

    private setUserId(userId: string) {
        this.firebase.setUserId(this.firebase.analytics, userId);
    }

    createEventSearch(searchString: string, id: string = Param.FA_SEARCH_TERM): void {
        this.sendEvent(Event.SEARCH, {
            [Param.ITEM_ID]: id,
            [Param.FA_ITEM_ID]: id,
            [Param.FA_SEARCH_TERM]: searchString,
        });
    }

    createScreenEvent(screenName: ScreenName, screenClass?: string): void {
        this.sendEvent(Event.SCREEN_VIEW, {
            [Param.FA_SCREEN_NAME]: screenName,
            [Param.FA_SCREEN_CLASS]: screenClass || screenName,
        });
    }

    createCustomScreenEvent(data: AnalyticsData): void {
        const payload: Record<string, any> = {
            [Param.FA_SCREEN_CLASS]: data[Param.FA_SCREEN_CLASS] || data[Param.FA_SCREEN_NAME],
        };
        Object.entries(data).forEach(([key, value]) => {
            if (value) {
                payload[key] = value;
            }
        });
    
        this.sendEvent(Event.SCREEN_VIEW, payload);
    }

    createOpenAppEvent(screenName: ScreenName, screenClass?: string): void {
        const UserInfoStoreModule = useUserInfoStore();

        const {
            isClient,
            getUserLogin
        } = storeToRefs(UserInfoStoreModule);

        if (isClient.value && getUserLogin.value) {
            this.setUserId(getUserLogin.value);
        }
        this.sendEvent(Event.APP_OPEN, {
            [Param.FA_SCREEN_NAME]: screenName,
            [Param.FA_SCREEN_CLASS]: screenClass || screenName,
        });
    }
    
    createEventLogin(loginId: string | null): void {
        if (loginId) {
            this.setUserId(loginId);
            this.sendEvent(Event.LOGIN, {
                [Param.FA_ITEM_ID]: loginId,
            });
        }
    }
}
